import type { LoaderFunctionArgs } from '~/types/route-args';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import React from 'react';
import PaginatedArticles from '@modules/articles/component/paginated-articles/PaginatedArticles';

import { redirect, useLoaderData } from 'react-router';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { fillExtraInfoForArticleCategory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import { response404 } from '~/modules/root/graphql/assertResponse.server';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import PageSections from '~/modules/professional/component/page-layout/PageSections';
import { shuffleArray } from '~/utils/arrayHelper';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getPreloadImageLink } from '~/modules/images';
import {
    createMetaDescription,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { createMetaTitle } from '~/modules/seo';
import type { APHandle } from '~/modules/root';
import { getArticleCategoryItems } from '~/modules/articles/api/get-article-category-items.server';
import { getDirectoryFeaturedArticleIDs } from '~/modules/articles/api/get-directory-featured-article-ids.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import { TOP_ARTICLES_COUNT } from '~/modules/articles/api/get-article-directory.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { ArticleCategoryRevamp } from '@modules/articles-revamp/component/ArticleCategoryRevamp';
import {
    EDITORIAL_GROUND,
    EDITORIAL_STATEMENT_FALLBACK,
} from '@modules/articles-revamp/config/editorial-ground';
import { isArticlesRevampEnabled } from '@modules/articles-revamp/util/is-articles-revamp-enabled.server';
import { getDigitalPublicationOptions } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { placeholderDigitalPublications } from '@modules-rocco/subscription/config/placeholder-publications';
import { getRecentMagazineTiles } from '~/modules/magazine/api/get-recent-magazines.server';
import { buildArticlesFilterState } from '@modules/articles-revamp/api/build-articles-filter-state.server';
import { getEditorialThemes } from '@modules/articles-revamp/api/get-editorial-themes.server';
import { getEditorialThemeBySegment } from '@modules/articles-revamp/api/get-editorial-theme.server';
import { alsoInCategoryId } from '@modules/articles-revamp/util/also-in-options';
import {
    hasActiveArticleFacets,
    ALSO_IN_FILTER_KEY,
} from '@modules-rocco/articles/config/article-facets';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;

        // A theme is not a CMS page, so there is no localisation for its path
        // and the candidates below fall through to the whole directory's copy.
        // These pages are indexable by default, so without the theme's own name
        // first the document title, description and OG tags all describe
        // /articles while the page itself is headed "Interviews".
        const theme = data?.theme;

        const title = createMetaTitle(
            props,
            [
                theme?.title,
                data?.localisation?.Title,
                data?.localisation?.DirectoryHeader?.Title,
            ],
            true,
            data?.pagination?.page,
        );

        const description = createMetaDescription([
            theme
                ? `Articles on ${theme.title} from expert writers and industry professionals on ArchiPro.`
                : undefined,
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const bannerImage =
            data?.articleCategory?.topReadArticles &&
            data?.articleCategory?.topReadArticles.length > 0
                ? data.articleCategory.topReadArticles[0]?.image.src
                : undefined;

        const preloadImageLinks = bannerImage
            ? data?.deviceType === 'mobile'
                ? getPreloadImageLink(undefined, bannerImage)
                : getPreloadImageLink(bannerImage)
            : [];

        return [
            { title },
            { name: 'description', content: description },
            // Theme pages are indexable by default; the switch is a staff-only
            // opt-out on the theme itself, for one too thin to carry a page.
            ...(data?.theme?.noIndex
                ? [{ name: 'robots', content: 'noindex, follow' }]
                : []),
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    fadeTopNavAfterPosition: 200,
    // The directory's header, exactly: solid, the logomark, the editorial
    // paper under it. Loader data rather than the flag directly, so the legacy
    // page is untouched when it is off.
    header: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? { showLogomark: true, ground: EDITORIAL_GROUND }
            : undefined,
    // The same paper on the shell's wrapper, server-rendered, so nothing
    // white shows between or behind the sections that carry it.
    contentClassName: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? EDITORIAL_GROUND
            : undefined,
};

/** The whole editorial directory — what a theme page lists, narrowed to itself. */
const ARTICLES_ROOT_LINK = '/articles';

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { device, user } = context;
    const requestUrl = request.url;
    const defaultPageSize = 36;

    // The cross-category facet travels as a URL segment, and only the nav tree
    // can say which category that is — so the nav is fetched first when the
    // facet is active, and alongside everything else when it is not.
    const alsoInSegment = new URL(request.url).searchParams.get(
        ALSO_IN_FILTER_KEY,
    );
    const navItemsFirst = alsoInSegment
        ? await getArticleNavigationItems(context)
        : undefined;
    const alsoInCategoryID = navItemsFirst
        ? alsoInCategoryId(
              navItemsFirst,
              new URL(request.url).pathname,
              alsoInSegment,
          )
        : undefined;

    // Read once, up here, because everything the revamp adds to this loader
    // hangs off it: themes are a revamp surface, and with the flag off this
    // route has to behave exactly as it did before the revamp existed.
    const showRevamp = isArticlesRevampEnabled(context, request);

    // The rail's search field is the directory's: a term committed on a
    // category page searches the whole library, from the root, where the
    // matches are listed. The category query takes no keyword.
    const searchTerm = new URL(request.url).searchParams.get('search')?.trim();
    if (showRevamp && searchTerm) {
        return redirect(
            `${ARTICLES_ROOT_LINK}?search=${encodeURIComponent(searchTerm)}`,
        );
    }

    // Themes share the category URL namespace, so `/articles/interviews` reads
    // like `/articles/lifestyle`. Category is resolved first and always wins;
    // the theme lookup only runs when no category answers to the segment.
    //
    // Behind the gate, so a theme segment stays a 404 while the flag is off
    // rather than quietly resolving and rendering through the legacy page --
    // the theme URL is part of the revamp, not a route that predates it.
    const pathSegments = new URL(request.url).pathname
        .replace(/^\/articles\/?/, '')
        .split('/')
        .filter(Boolean);
    const themeCandidate =
        showRevamp && pathSegments.length === 1 ? pathSegments[0] : '';

    // A link naming no category makes the category query throw rather than
    // return nothing, so the miss is caught and kept: if the segment turns out
    // to name no theme either, this is the error to surface, and swallowing it
    // outright would turn a genuine backend failure into a 404.
    let categoryError: unknown;

    // Everything this page can ask for without first knowing what it is
    // looking at. The magazine shelf and the publication catalogue are fixed
    // lists — neither reads the listing — so they belong here rather than in
    // the tail below, where they used to queue up behind it one at a time.
    const [
        articleCategory,
        localisation,
        bannerAds,
        navItems,
        heroPinnedArticleIDs,
        directoryLocalisation,
        magazineTiles,
        catalogue,
    ] = await Promise.all([
        (async () => {
            try {
                return await getArticleCategoryItems({
                    request: request,
                    ...context,
                    alsoInCategoryID,
                });
            } catch (error: unknown) {
                categoryError = error;

                return undefined;
            }
        })(),
        getPageLocalisation(args),
        adRequest({
            type: 'ArchizeenBannerPlacement',
            requestUrl,
            ...args.context,
        }),
        navItemsFirst ?? getArticleNavigationItems(context),
        // Only the tile menu reads this, and only the revamp renders one,
        // so the legacy category page pays nothing for it.
        showRevamp ? getDirectoryFeaturedArticleIDs(context) : [],
        // The directory's own copy, for the opening this page shares with it:
        // a theme page says what the library is, not what the theme is.
        showRevamp
            ? getPageLocalisation(args, { url: ARTICLES_ROOT_LINK })
            : null,
        // The last ten issues, for the magazine shelf.
        showRevamp ? getRecentMagazineTiles({ ...context, limit: 10 }) : [],
        // The publications row's catalogue, as the directory root fetches it.
        showRevamp ? getDigitalPublicationOptions(context) : [],
    ]);

    // An empty listing means one of two different things now that the revamp
    // has facets: a category that does not exist, or filters that happen to
    // match nothing. Only the first is a 404 — 404ing the second would tell the
    // reader the page moved and throw away the filters they had just set.
    const hasActiveFacets =
        showRevamp && hasActiveArticleFacets(new URL(request.url).searchParams);
    // Three outcomes now, not two: a category, a theme, or neither. Only the
    // last is a 404. A theme page that filters to nothing still renders — the
    // same rule the facets already follow.
    const theme =
        !articleCategory && themeCandidate
            ? await getEditorialThemeBySegment(context, themeCandidate)
            : undefined;

    // A theme page lists the whole directory, so the facet themes it offers
    // are the whole directory's — they do not wait on its listing the way a
    // category's do, and the two go together.
    const [themeListing, themeFacetThemes] = theme
        ? await Promise.all([
              getArticleCategoryItems({
                  request: request,
                  ...context,
                  linkOverride: ARTICLES_ROOT_LINK,
                  forcedThemes: [theme.segment],
                  // A theme page offers "Also in" like a category page does,
                  // and the selection travels in the URL either way. Without
                  // it here the chip read as applied while the grid ignored it.
                  alsoInCategoryID,
              }),
              getEditorialThemes(context, 0),
          ])
        : [undefined, undefined];

    const listing = articleCategory ?? themeListing;

    if (!listing) {
        throw categoryError ?? response404();
    }

    // The one call that genuinely waits on the listing: a category's facets are
    // scoped to the category being browsed, so a group whose every theme holds
    // nothing here does not render at all. A theme page's came back above.
    const themes = showRevamp
        ? (themeFacetThemes ??
          (await getEditorialThemes(context, listing.categoryID ?? 0)))
        : [];

    if (!theme && !hasActiveFacets && !listing.articles.length) {
        throw response404();
    }

    fillExtraInfoForArticleCategory(user, listing);

    const pagination = getPaginationOpts({
        request,
        device,
        defaultPageSize,
        totalCount: listing.totalCount || 0,
    });

    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const featuredSlides = bannerAdsShuffled?.length
        ? bannerAdsShuffled
              .slice(0, TOP_ARTICLES_COUNT)
              .map(mapAdDataToFeatureSlide)
        : (listing.topReadArticles ?? []);

    // Outside production an empty catalogue falls back to placeholders so the
    // row stays reviewable.
    const publications =
        showRevamp && catalogue.length === 0
            ? placeholderDigitalPublications(context.env.stage)
            : catalogue;

    return {
        deviceType: context.device.type,
        // The resolved listing, whether it came from a category or a theme. The
        // page renders the same template either way.
        articleCategory: listing,
        /** Set when the segment named a theme rather than a category. */
        theme,
        localisation,
        bannerAds: bannerAdsShuffled,
        pagination,
        navItems,
        featuredSlides,
        // The tile menu reads this to label itself Pin or Unpin. A pick is
        // global to the directory, so a category page pins to the same hero.
        heroPinnedArticleIDs,
        showRevamp,
        publications,
        editions: magazineTiles,
        themes,
        statement:
            directoryLocalisation?.Description ?? EDITORIAL_STATEMENT_FALLBACK,
        // The listing's own facet states. Built from the URL only — the
        // "Published" presets are a fixed vocabulary, so nothing is aggregated.
        filterStates: showRevamp
            ? {
                  ...buildArticlesFilterState({
                      context,
                      request,
                      navItems: navItems ?? [],
                      themes,
                  }),
                  totalCount: listing.totalCount ?? 0,
              }
            : undefined,
    };
};

const ArchizeenCategoryPage = () => {
    const {
        articleCategory,
        pagination,
        localisation,
        navItems,
        featuredSlides,
        showRevamp,
        editions,
        filterStates,
        themes,
        heroPinnedArticleIDs,
        statement,
        publications,
        deviceType,
    } = useLoaderData<typeof loader>();
    const { topReadArticles: _, ...rest } = articleCategory;
    const { common } = useLoaderData<typeof archizeenRootLoader>();

    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);
    const showFeaturedCarousel =
        !articleCategory.useFilmsCategoryLayout &&
        featuredSlidesMemoized.length > 0;

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    // Gate sits below the hooks so both paths run the same hook order.
    if (showRevamp && filterStates) {
        return (
            <>
                <ArticleCategoryRevamp
                    articles={rest.articles}
                    totalCount={rest.totalCount ?? rest.articles.length}
                    pageSize={rest.pageSize}
                    navItems={navItems ?? []}
                    statement={statement}
                    editions={editions}
                    filterStates={filterStates}
                    themes={themes}
                    heroPinnedArticleIDs={heroPinnedArticleIDs}
                    publications={publications}
                    // The category pages' filter chip and bottom sheet, for
                    // phones. A server fact (mobile is UA, not viewport), so
                    // the desktop rail keeps its chipless composition.
                    showMobileFilters={deviceType === 'mobile'}
                />
            </>
        );
    }

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={
                    localisation?.DirectoryHeader?.ContentTop ||
                    common?.contentTop
                }
                title={localisation?.DirectoryHeader?.Title}
            />
            <PageSections>
                {showFeaturedCarousel && (
                    <FeaturedCarousel
                        featuredSlides={featuredSlidesMemoized}
                        imageLazyLoading={'eager'}
                    />
                )}
                <PaginatedArticles {...rest} {...pagination} />
            </PageSections>
        </>
    );
};

export const shouldRevalidate = shouldRevalidateLeaf;

export default ArchizeenCategoryPage;
